// src/components/ParametresOrganigramme.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Award,
  BarChart3,
  BookOpen,
  Check,
  DollarSign,
  Download,
  Edit3,
  Eye,
  FileText,
  GraduationCap,
  Handshake,
  Network,
  Plus,
  Save,
  Trash2,
  Upload,
  UserCheck,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { api } from "../services/api";
import useOrganigrammeParams from "../hooks/useOrganigrammeParams";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Toast } from "./ui/Toast";
import EnTeteParametrage from "./parametrage/EnTeteParametrage";
import CarteReglages from "./parametrage/CarteReglages";
import ChampSelect from "./ui/ChampSelect";
import ConfirmDialog from "./ConfirmDialog";

const ICONS = {
  Users,
  Award,
  BarChart3,
  DollarSign,
  GraduationCap,
  BookOpen,
  UserCheck,
  FileText,
  Wallet,
  Handshake,
};

const iconOptions = [
  "Users",
  "Award",
  "BarChart3",
  "DollarSign",
  "GraduationCap",
  "BookOpen",
  "UserCheck",
  "FileText",
  "Wallet",
  "Handshake",
];

const colorOptions = [
  { label: "Bleu Klasys", value: "from-[#246990] to-[#3084b5]" },
  { label: "Violet", value: "from-[#8B5CF6] to-[#A855F7]" },
  { label: "Terracotta", value: "from-[#c66d59] to-[#ae7467]" },
  { label: "Turquoise", value: "from-[#319190] to-[#63b5b4]" },
  { label: "Bleu clair", value: "from-[#3eb5da] to-[#5bc5e4]" },
  { label: "Bleu", value: "from-blue-500 to-blue-600" },
  { label: "Teal", value: "from-teal-500 to-teal-600" },
  { label: "Orange", value: "from-orange-500 to-orange-600" },
  { label: "Gris", value: "from-gray-600 to-gray-700" },
  { label: "Ambre", value: "from-amber-500 to-amber-600" },
];

const emptyForm = {
  parent_id: "",
  titre: "",
  type: "interne",
  employe_id: "",
  nom_affiche: "",
  description: "",
  niveau: 1,
  ordre: 0,
  icone: "Users",
  couleur_fond: "from-[#246990] to-[#3084b5]",
  couleur_texte: "#ffffff",
  actif: true,
  dans_hierarchie: false,
};

const emptyReglementForm = {
  titre: "Règlement intérieur",
  description: "",
  version: "",
  contenu: "",
  fichier: null,
};

const formatFileSize = (size) => {
  if (!size) return "-";
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

const getItemName = (item) => {
  return item.employe?.nom_complet || item.nom_affiche || "Non renseigne";
};

const hasTextContent = (doc) => Boolean(doc?.contenu?.trim());
const hasAttachedFile = (doc) => Boolean(doc?.fichier_url);

const getReglementExcerpt = (doc) => {
  const content = doc?.contenu?.trim();

  if (!content) return "Aucun contenu redige.";

  return content.length > 180 ? `${content.slice(0, 180)}...` : content;
};

const OrgPreviewCard = ({ item }) => {
  const Icon = ICONS[item.icone] || Users;

  return (
    <div className={`flex items-center gap-3 bg-gradient-to-r ${item.couleur_fond || "from-[#246990] to-[#3084b5]"} text-white px-4 py-3 rounded-lg shadow-sm min-w-56`}>
      <div className="w-11 h-11 bg-white rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
        {item.employe?.photo_url ? (
          <img src={item.employe.photo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <Icon size={20} className="text-gray-500" />
        )}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium truncate">{item.titre}</div>
        <div className="text-sm font-semibold truncate">{getItemName(item)}</div>
        {item.description && <div className="text-xs opacity-90 truncate">{item.description}</div>}
      </div>
    </div>
  );
};

const ParametresOrganigramme = ({ onBackToDashboard }) => {
  const {
    organigramme,
    organigrammeByLevel,
    reglementActif,
    reglements,
    loading,
    loadingReglement,
    error,
    reglementError,
    addOrganigrammeItem,
    updateOrganigrammeItem,
    deleteOrganigrammeItem,
    uploadReglement,
    activateReglement,
    deleteReglement,
    downloadReglementPdf,
    downloadReglementFile,
    fetchReglementFilePreview,
  } = useOrganigrammeParams();

  const [activeTab, setActiveTab] = useState("organigramme");
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // Les dix-sept messages de cette page s'affichaient tous dans le meme bandeau
  // bleu : « Element supprime. » et « Erreur lors de la suppression. » se
  // ressemblaient trait pour trait. Un echec doit se distinguer d'un succes
  // sans avoir a lire.
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  // Ce qui attend une confirmation : { type: "element" | "reglement", id, nom }
  const [aConfirmer, setAConfirmer] = useState(null);
  const afficher = (message, type = "success") =>
    setToast({ show: Boolean(message), message: message || "", type });

  const [reglementForm, setReglementForm] = useState(emptyReglementForm);
  const [uploadingReglement, setUploadingReglement] = useState(false);
  const [previewReglement, setPreviewReglement] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loadingFilePreviewId, setLoadingFilePreviewId] = useState(null);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const response = await api.employes.getAll();
        if (response.success) {
          setEmployees(response.data || []);
        }
      } catch (err) {
        console.error("Erreur chargement employes:", err);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  useEffect(() => {
    return () => {
      if (filePreview?.url) {
        URL.revokeObjectURL(filePreview.url);
      }
    };
  }, [filePreview?.url]);

  const levels = useMemo(() => {
    const keys = Object.keys(organigrammeByLevel || {});
    return keys.sort((a, b) => Number(a) - Number(b));
  }, [organigrammeByLevel]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      parent_id: item.parent_id || "",
      titre: item.titre || "",
      type: item.type || "interne",
      employe_id: item.employe_id || "",
      nom_affiche: item.nom_affiche || "",
      description: item.description || "",
      niveau: item.niveau || 1,
      ordre: item.ordre || 0,
      icone: item.icone || "Users",
      couleur_fond: item.couleur_fond || "from-[#246990] to-[#3084b5]",
      couleur_texte: item.couleur_texte || "#ffffff",
      actif: Boolean(item.actif),
      dans_hierarchie: Boolean(item.dans_hierarchie),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setToast({ show: false, message: "", type: "success" });

    if (!form.titre.trim()) {
      afficher("Le titre est requis.", "error");
      return;
    }

    setSaving(true);

    const payload = {
      ...form,
      parent_id: form.parent_id || null,
      employe_id: form.type === "interne" ? form.employe_id || null : null,
      nom_affiche: form.type !== "interne" ? form.nom_affiche || form.titre : form.nom_affiche || null,
      niveau: Number(form.niveau),
      ordre: Number(form.ordre),
      actif: Boolean(form.actif),
      dans_hierarchie: form.type === "externe" ? Boolean(form.dans_hierarchie) : false,
    };

    const result = editingId
      ? await updateOrganigrammeItem(editingId, payload)
      : await addOrganigrammeItem(payload);

    setSaving(false);

    if (!result.success) {
      afficher(result.error || "Erreur lors de l'enregistrement.", "error");
      return;
    }

    afficher(editingId ? "Élément mis à jour." : "Élément ajouté.");
    resetForm();
  };

  // `window.confirm` bloque le navigateur, ne se style pas et sort du systeme
  // de dialogues de l'application. CLAUDE.md demande `ConfirmDialog`.
  const handleDelete = async (id) => {
    const result = await deleteOrganigrammeItem(id);

    if (!result.success) {
      afficher(result.error || "Erreur lors de la suppression.", "error");
      return;
    }

    afficher("Élément supprimé.");
    if (editingId === id) resetForm();
  };

  const handleUploadReglement = async (event) => {
    event.preventDefault();
    setToast({ show: false, message: "", type: "success" });

    if (!reglementForm.contenu.trim() && !reglementForm.fichier) {
      afficher("Rédigez un contenu ou joignez un fichier avant d'enregistrer.", "error");
      return;
    }

    setUploadingReglement(true);

    const result = await uploadReglement(reglementForm);

    setUploadingReglement(false);

    if (!result.success) {
      afficher(result.error || "Erreur lors de l'enregistrement du règlement.", "error");
      return;
    }

    setReglementForm(emptyReglementForm);

    const input = document.getElementById("reglement-file");
    if (input) input.value = "";

    afficher("Règlement intérieur enregistré.");
  };

  const handleActivateReglement = async (id) => {
    const result = await activateReglement(id);
    afficher(result.success ? "Règlement activé." : result.error || "Erreur lors de l'activation.", result.success ? "success" : "error");
  };

  const handleDeleteReglement = async (id) => {
    const result = await deleteReglement(id);
    afficher(result.success ? "Règlement supprimé." : result.error || "Erreur lors de la suppression.", result.success ? "success" : "error");
  };

  const handleDownloadPdf = async (doc) => {
    const result = await downloadReglementPdf(doc.id);

    if (!result.success) {
      afficher(result.error || "Erreur lors du téléchargement du PDF.", "error");
    }
  };

  const handleDownloadFile = async (doc) => {
    const result = await downloadReglementFile(doc.id);

    if (!result.success) {
      afficher(result.error || "Erreur lors du téléchargement du fichier joint.", "error");
    }
  };

  const handlePreviewFile = async (doc) => {
    if (!hasAttachedFile(doc)) {
      afficher("Aucun fichier joint pour ce règlement.", "error");
      return;
    }

    setToast({ show: false, message: "", type: "success" });
    setLoadingFilePreviewId(doc.id);

    const result = await fetchReglementFilePreview(doc.id);

    setLoadingFilePreviewId(null);

    if (!result.success) {
      afficher(result.error || "Erreur lors de l'ouverture du fichier joint.", "error");
      return;
    }

    setFilePreview({
      ...result,
      document: doc,
    });
  };

  const closeFilePreview = () => {
    setFilePreview(null);
  };

  return (
    <div className="w-full">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={Network}
        titre="Paramétrage de l'organigramme"
        sousTitre="Structure hiérarchique de l'école et règlement intérieur"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 mb-6 bg-[#246990]/8 p-1 rounded-xl">
          <TabsTrigger
            value="organigramme"
            className="data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <Network size={15} className="mr-2" />
            <span>Structure hiérarchique</span>
          </TabsTrigger>
          <TabsTrigger
            value="reglement"
            className="data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm rounded-lg text-gray-600 text-sm font-semibold transition-all"
          >
            <FileText size={15} className="mr-2" />
            <span>Règlement intérieur</span>
          </TabsTrigger>
        </TabsList>

        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <TabsContent value="organigramme" className="mt-0 focus-visible:outline-none">
        <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6">
          <CarteReglages
            titre={editingId ? "Modifier un élément" : "Ajouter un élément"}
            accent="#246990"
          >

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre du poste *</label>
                <input
                  type="text"
                  value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                  placeholder="Directeur General"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <ChampSelect
                    valeur={form.type}
                    onChange={(nextType) =>
                      setForm({
                        ...form,
                        type: nextType,
                        employe_id: "",
                        dans_hierarchie: nextType === "externe" ? form.dans_hierarchie : false,
                      })
                    }
                    options={[
                      { valeur: "interne", libelle: "Employé interne" },
                      { valeur: "externe", libelle: "Intervenant externe" },
                      { valeur: "groupe", libelle: "Groupe" },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icone</label>
                  <ChampSelect
                    valeur={form.icone}
                    onChange={(valeur) => setForm({ ...form, icone: valeur })}
                    options={iconOptions.map((icon) => ({ valeur: icon, libelle: icon }))}
                  />
                </div>
              </div>

              {form.type === "interne" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employe lie</label>
                  <ChampSelect
                    valeur={form.employe_id || ""}
                    onChange={(valeur) => setForm({ ...form, employe_id: valeur })}
                    desactive={loadingEmployees}
                    placeholder={loadingEmployees ? "Chargement…" : "Aucun employé sélectionné"}
                    options={employees.map((employee) => ({
                      valeur: employee.id,
                      libelle:
                        [employee.prenom, employee.nom].filter(Boolean).join(" ") +
                        (employee.role_nom ? ` — ${employee.role_nom}` : ""),
                    }))}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom affiche</label>
                  <input
                    type="text"
                    value={form.nom_affiche}
                    onChange={(e) => setForm({ ...form, nom_affiche: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Nom de la personne ou du groupe"
                  />
                </div>
              )}

              {form.type === "externe" && (
                <div className="border border-blue-100 bg-blue-50 rounded-lg px-3 py-3">
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.dans_hierarchie}
                      onChange={(e) => setForm({ ...form, dans_hierarchie: e.target.checked })}
                      className="w-4 h-4 mt-0.5"
                    />
                    <span>
                      <span className="block font-medium text-[#246990]">
                        Afficher dans la hierarchie decisionnelle
                      </span>
                      <span className="block text-xs text-gray-600 mt-1">
                        Coche si cette personne externe doit apparaitre dans le diagramme principal.
                      </span>
                    </span>
                  </label>
                </div>
              )}


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Direction operationnelle, Gestion financiere..."
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={form.niveau}
                    onChange={(e) => setForm({ ...form, niveau: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                  <input
                    type="number"
                    min="0"
                    value={form.ordre}
                    onChange={(e) => setForm({ ...form, ordre: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 h-10 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.actif}
                      onChange={(e) => setForm({ ...form, actif: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Actif
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                <ChampSelect
                  valeur={form.couleur_fond}
                  onChange={(valeur) => setForm({ ...form, couleur_fond: valeur })}
                  options={colorOptions.map((color) => ({
                    valeur: color.value,
                    libelle: color.label,
                  }))}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#246990] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#1a4d6e] disabled:opacity-60"
                >
                  <Save size={16} />
                  {saving ? "Enregistrement..." : editingId ? "Mettre a jour" : "Ajouter"}
                </button>

                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center justify-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    <X size={16} />
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </CarteReglages>

          <CarteReglages
            titre="Configuration actuelle"
            accent="#319190"
            compteur={`${organigramme.length} élément${organigramme.length > 1 ? "s" : ""}`}
          >
            <div>
              {loading && <p className="text-sm text-gray-500">Chargement...</p>}
              {error && <p className="text-sm text-red-600">Erreur : {error}</p>}

              {!loading && !error && organigramme.length === 0 && (
                <div className="text-center border border-dashed border-gray-300 rounded-lg p-10">
                  <Network size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="font-medium text-gray-700">Aucun élément configuré</p>
                  <p className="text-sm text-gray-500">Ajoute les postes depuis le formulaire.</p>
                </div>
              )}

              <div className="space-y-6">
                {levels.map((level) => (
                  <div key={level}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                      Niveau {level}
                    </h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {(organigrammeByLevel[level] || []).map((item) => (
                        <div key={item.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex gap-3">
                            <OrgPreviewCard item={item} />

                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={() => handleEdit(item)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Modifier"
                              >
                                <Edit3 size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setAConfirmer({ type: "element", id: item.id, nom: item.titre })}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Supprimer"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                            <span>
                              {item.type}
                              {item.type === "externe" && item.dans_hierarchie ? " - Hierarchie" : ""}
                            </span>
                            <span>{item.actif ? "Actif" : "Inactif"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CarteReglages>
        </div>
        </TabsContent>

        <TabsContent value="reglement" className="mt-0 focus-visible:outline-none">
        <div className="grid grid-cols-1 xl:grid-cols-[460px_1fr] gap-6">
          <CarteReglages
            titre="Rédiger le règlement"
            accent="#246990"
          >

            <form onSubmit={handleUploadReglement} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={reglementForm.titre}
                  onChange={(e) => setReglementForm({ ...reglementForm, titre: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                <input
                  type="text"
                  value={reglementForm.version}
                  onChange={(e) => setReglementForm({ ...reglementForm, version: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="2026, V1, Annee scolaire..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description courte</label>
                <textarea
                  value={reglementForm.description}
                  onChange={(e) => setReglementForm({ ...reglementForm, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  rows={3}
                  placeholder="Document applicable au personnel..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu du règlement</label>
                <textarea
                  value={reglementForm.contenu}
                  onChange={(e) => setReglementForm({ ...reglementForm, contenu: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y min-h-[260px] leading-relaxed"
                  placeholder={"Article 1 - Objet\n\nLe present règlement intérieur definit les regles de fonctionnement...\n\nArticle 2 - Horaires\n\n..."}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Les sauts de ligne seront conserves dans la consultation et dans le PDF.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fichier joint optionnel</label>
                <input
                  id="reglement-file"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setReglementForm({ ...reglementForm, fichier: e.target.files?.[0] || null })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">PDF, DOC ou DOCX. Taille maximale : 10 Mo.</p>
              </div>

              <button
                type="submit"
                disabled={uploadingReglement}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#246990] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#1a4d6e] disabled:opacity-60"
              >
                <Save size={16} />
                {uploadingReglement ? "Enregistrement..." : "Enregistrer le reglement"}
              </button>
            </form>
          </CarteReglages>

          <CarteReglages
            titre="Règlements enregistrés"
            accent="#319190"
          >

            <div className="">
              {loadingReglement && <p className="text-sm text-gray-500">Chargement...</p>}
              {reglementError && <p className="text-sm text-red-600">Erreur : {reglementError}</p>}

              {!loadingReglement && !reglementError && reglements.length === 0 && (
                <div className="text-center border border-dashed border-gray-300 rounded-lg p-10">
                  <FileText size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="font-medium text-gray-700">Aucun règlement enregistré</p>
                  <p className="text-sm text-gray-500">Redige un contenu ou ajoute un fichier pour l'activer dans la RH.</p>
                </div>
              )}

              {reglementActif && (
                <div className="mb-5 border border-green-200 bg-green-50 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                        <Check size={16} />
                        Reglement actif
                      </p>
                      <p className="font-medium text-gray-900 mt-1">{reglementActif.titre}</p>
                      <p className="text-sm text-gray-600 max-h-10 overflow-hidden">
                        {getReglementExcerpt(reglementActif)}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        {hasTextContent(reglementActif) && (
                          <span className="bg-white text-green-700 px-2 py-0.5 rounded-full border border-green-200">
                            Contenu redige
                          </span>
                        )}
                        {hasAttachedFile(reglementActif) && (
                          <span className="bg-white text-green-700 px-2 py-0.5 rounded-full border border-green-200">
                            Fichier joint
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewReglement(reglementActif)}
                        className="p-2 text-[#246990] hover:bg-white rounded-lg"
                        title="Lire le reglement"
                      >
                        <Eye size={17} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(reglementActif)}
                        className="p-2 text-[#246990] hover:bg-white rounded-lg"
                        title="Télécharger le PDF genere"
                      >
                        <Download size={17} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {reglements.map((doc) => (
                  <div key={doc.id} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText size={17} className="text-[#246990] flex-shrink-0" />
                        <p className="font-medium text-gray-900 truncate">{doc.titre}</p>
                        {doc.actif ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Actif</span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Inactif</span>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 mt-1 max-h-10 overflow-hidden">
                        {getReglementExcerpt(doc)}
                      </p>

                      <p className="text-xs text-gray-500 mt-2">
                        {doc.version ? `Version ${doc.version} - ` : ""}
                        {doc.nom_fichier_original ? `${doc.nom_fichier_original} - ${formatFileSize(doc.taille_fichier)}` : "Aucun fichier joint"}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewReglement(doc)}
                        className="p-2 text-[#246990] hover:bg-blue-50 rounded-lg"
                        title="Lire le contenu"
                      >
                        <Eye size={16} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(doc)}
                        className="p-2 text-[#246990] hover:bg-blue-50 rounded-lg"
                        title="Télécharger le PDF genere"
                      >
                        <Download size={16} />
                      </button>

                      {hasAttachedFile(doc) && (
                        <>
                          <button
                            type="button"
                            onClick={() => handlePreviewFile(doc)}
                            disabled={loadingFilePreviewId === doc.id}
                            className="p-2 text-[#246990] hover:bg-blue-50 rounded-lg disabled:opacity-50"
                            title="Ouvrir le fichier joint"
                          >
                            <FileText size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDownloadFile(doc)}
                            className="p-2 text-[#246990] hover:bg-blue-50 rounded-lg"
                            title="Télécharger le fichier joint"
                          >
                            <Upload size={16} />
                          </button>
                        </>
                      )}

                      {!doc.actif && (
                        <button
                          type="button"
                          onClick={() => handleActivateReglement(doc.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Activer"
                        >
                          <Check size={16} />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setAConfirmer({ type: "reglement", id: doc.id, nom: doc.titre })}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CarteReglages>
        </div>
        </TabsContent>
        </div>
      </Tabs>

      <ConfirmDialog
        isOpen={Boolean(aConfirmer)}
        onClose={() => setAConfirmer(null)}
        onConfirm={() => {
          const cible = aConfirmer;
          setAConfirmer(null);
          if (!cible) return;
          if (cible.type === "element") handleDelete(cible.id);
          else handleDeleteReglement(cible.id);
        }}
        title={aConfirmer?.type === "reglement" ? "Supprimer le règlement" : "Supprimer l'élément"}
        description={`Êtes-vous sûr de vouloir supprimer « ${aConfirmer?.nom || ""} » ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />

      {previewReglement && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="border-b px-5 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-semibold text-[#246990] truncate">{previewReglement.titre}</h3>
                <p className="text-xs text-gray-500">
                  {previewReglement.version ? `Version ${previewReglement.version}` : "Sans version"}
                  {previewReglement.nom_fichier_original ? ` - Fichier joint : ${previewReglement.nom_fichier_original}` : ""}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPreviewReglement(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              {previewReglement.description && (
                <div className="mb-4 border-l-4 border-[#246990] bg-blue-50 px-4 py-3 text-sm text-gray-700">
                  {previewReglement.description}
                </div>
              )}

              <div className="whitespace-pre-wrap text-sm leading-7 text-gray-800">
                {previewReglement.contenu?.trim() || "Aucun contenu redige pour ce reglement."}
              </div>
            </div>

            <div className="border-t px-5 py-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleDownloadPdf(previewReglement)}
                className="inline-flex items-center gap-2 bg-[#246990] text-white rounded-lg px-4 py-2 text-sm hover:bg-[#1a4d6e]"
              >
                <Download size={16} />
                PDF
              </button>

              {hasAttachedFile(previewReglement) && (
                <button
                  type="button"
                  onClick={() => handlePreviewFile(previewReglement)}
                  className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm hover:bg-gray-50"
                >
                  <FileText size={16} />
                  Fichier joint
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {filePreview && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
            <div className="border-b px-5 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-semibold text-[#246990] truncate">
                  {filePreview.filename || filePreview.document?.nom_fichier_original || "Fichier joint"}
                </h3>
                <p className="text-xs text-gray-500">{filePreview.document?.titre}</p>
              </div>

              <button
                type="button"
                onClick={closeFilePreview}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 bg-gray-100">
              {filePreview.mimeType?.includes("pdf") ? (
                <iframe
                  src={filePreview.url}
                  title={filePreview.filename || "Fichier joint"}
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="h-full flex items-center justify-center p-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-8 max-w-md text-center">
                    <FileText size={42} className="mx-auto text-[#246990] mb-3" />
                    <p className="font-semibold text-gray-800 mb-2">Aperçu indisponible</p>
                    <p className="text-sm text-gray-600 mb-4">
                      Ce format de fichier ne peut pas etre affiche directement dans le navigateur.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleDownloadFile(filePreview.document)}
                      className="inline-flex items-center gap-2 bg-[#246990] text-white rounded-lg px-4 py-2 text-sm hover:bg-[#1a4d6e]"
                    >
                      <Download size={16} />
                      Télécharger le fichier
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametresOrganigramme;